import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import axios from 'axios'
import toastr from "../../../utils/toast";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const TRIP_TYPES = [
  { key: 'oneway', label: 'One Way'    },
  { key: 'round',  label: 'Round Trip' },
  { key: 'multi',  label: 'Multi-city' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
let _legId = 0
const newLeg = () => ({
  id:         ++_legId,
  cityFrom:   '',
  cityTo:     '',
  country:    '',        // stores country_id (number)
  departure:  '',
  returnDate: '',
})

const isWeekend = (dateStr) => {
  if (!dateStr) return false
  const d = new Date(dateStr).getDay()
  return d === 0 || d === 6
}

const getWorkingDays = (from, to) => {
  if (!from || !to) return 0
  let count = 0
  const cur = new Date(from)
  const end = new Date(to)
  cur.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  while (cur <= end) {
    const d = cur.getDay()
    if (d !== 0 && d !== 6) count++
    cur.setDate(cur.getDate() + 1)
  }
  return count
}

// Build ONLY the fields the backend controller reads from req.body.
// Extra FE-only fields must NOT be sent — they leak into SET ? and cause a 500.
const buildPayload = (tripType, legs, employeeinfo_id, approver, notification, specialInstruction, imageBase64) => {
  const d = {
    employeeinfo_id,
    approver,
    notification,      // array — controller converts to comma string
  }

  // Special instruction → mapped to 'notes' column (matches PHP form field name)
  if (specialInstruction.trim()) {
    d.special_instruction = specialInstruction.trim()
  }

  // Pasted screenshot → sent as 'image'; controller renames to 'screenshot'
  if (imageBase64) {
    d.image = imageBase64
  }

  if (tripType === 'oneway') {
    d.way              = 'One Way'
    d.departure_date_1 = legs[0].departure
    d.arrival_date_1   = legs[0].departure
    d.city_from1       = legs[0].cityFrom
    d.city_1           = legs[0].cityTo
    d.country_id_1     = legs[0].country   // country_id from API

  } else if (tripType === 'round') {
    // Send as "Round Trip" — controller swaps dates/cities and converts to Multicity
    // PHP reads arrival_date_1 as the return date the user picked
    d.way              = 'Round Trip'
    d.departure_date_1 = legs[0].departure
    d.arrival_date_1   = legs[0].returnDate
    d.city_from1       = legs[0].cityFrom
    d.city_1           = legs[0].cityTo
    d.country_id_1     = legs[0].country

  } else {
    // Multicity — controller chains arrival dates itself
    d.way = 'Multicity'
    legs.forEach((leg, i) => {
      const n = i + 1
      d[`departure_date_${n}`] = leg.departure
      d[`city_from${n}`]       = leg.cityFrom
      d[`city_${n}`]           = leg.cityTo
      d[`country_id_${n}`]     = leg.country
    })
  }

  return d
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionDivider = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 12px' }}>
    <div style={{ flex: 1, height: 0.5, background: '#e5e7eb' }} />
    <span style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 0.5, background: '#e5e7eb' }} />
  </div>
)

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>
      {label}
    </label>
    {children}
  </div>
)

const inp = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 9,
  border: '1.5px solid #e5e7eb',
  background: '#f9fafb',
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: 13,
  color: '#111827',
  boxSizing: 'border-box',
}

// ─── Leg Row ──────────────────────────────────────────────────────────────────
const LegRow = ({ leg, index, tripType, showRemove, onUpdate, onRemove, countries, countriesLoading }) => {
  const isMulti    = tripType === 'multi'
  const showReturn = tripType === 'round' && index === 0

  const handle = (key) => (e) => {
    const val = e.target.value
    if ((key === 'departure' || key === 'returnDate') && isWeekend(val)) return
    onUpdate(leg.id, key, val)
  }

  return (
    <div style={isMulti ? {
      background: '#f9fafb', border: '1.5px solid #e5e7eb',
      borderRadius: 12, padding: 14, marginBottom: 10,
    } : { marginBottom: 4 }}>

      {isMulti && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Leg {index + 1}
          </span>
          {showRemove && (
            <button
              type="button"
              onClick={() => onRemove(leg.id)}
              style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Remove
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="From city">
          <input type="text" value={leg.cityFrom} onChange={handle('cityFrom')} placeholder="e.g. Delhi" style={inp} />
        </Field>
        <Field label="To city">
          <input type="text" value={leg.cityTo} onChange={handle('cityTo')} placeholder="e.g. Mumbai" style={inp} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showReturn ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>
        <Field label="Destination country">
          {/* Countries grouped by region using <optgroup> — matches API response structure */}
          <select
            value={leg.country}
            onChange={handle('country')}
            disabled={countriesLoading}
            style={{ ...inp, opacity: countriesLoading ? 0.6 : 1 }}
          >
            <option value="">{countriesLoading ? 'Loading...' : 'Select country'}</option>
            {countries.map(group => (
              <optgroup key={group.region_id} label={group.region_name}>
                {group.countries.map(c => (
                  <option key={c.country_id} value={c.country_id}>
                    {c.country}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>

        <Field label={isMulti ? 'Date' : 'Departure'}>
          <input type="date" value={leg.departure} onChange={handle('departure')} style={inp} />
        </Field>

        {showReturn && (
          <Field label="Return">
            <input type="date" value={leg.returnDate} min={leg.departure} onChange={handle('returnDate')} style={inp} />
          </Field>
        )}
      </div>
    </div>
  )
}

// ─── Paste Image Area ──────────────────────────────────────────────────────────
// Mirrors the PHP form's paste-screenshot field exactly.
// User presses Ctrl+V anywhere inside the box → image captured and previewed.
const PasteImageArea = ({ image, onImage }) => {
  const boxRef = useRef(null)

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items || []
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        const reader = new FileReader()
        reader.onload = (ev) => onImage(ev.target.result)  // base64 data URL
        reader.readAsDataURL(file)
        break
      }
    }
  }, [onImage])

  return (
    <div
      ref={boxRef}
      onPaste={handlePaste}
      tabIndex={0}  // makes the div focusable so it receives paste events
      style={{
        border: '1.5px dashed #d1d5db',
        borderRadius: 9,
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'text',
        outline: 'none',
        overflow: 'hidden',
        position: 'relative',
        background: '#f9fafb',
      }}
      onClick={() => boxRef.current?.focus()}
    >
      {image ? (
        <>
          <img
            src={image}
            alt="Travel approval"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onImage(null) }}
            style={{
              position: 'absolute', top: 6, right: 6,
              width: 22, height: 22, borderRadius: '50%',
              border: 'none', background: 'rgba(0,0,0,0.5)',
              color: '#fff', fontSize: 11, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {'\u2715'}
          </button>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>{'📋'}</div>
          <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', fontFamily: 'inherit' }}>
            Just One More Step.. Paste{' '}
            <span style={{ fontWeight: 600, color: '#6b7280' }}>(Ctrl + V)</span>{' '}
            your Travel Approval
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function AddTravelModal({
  show,
  setShow,
  employeeinfo_id,
  fetchTravels,
}) {
  const { token, setGlobalLoading } = useOutletContext()

  const [tripType,          setTripType]          = useState('round')
  const [legs,              setLegs]              = useState([newLeg()])
  const [approver,          setApprover]          = useState('')
  const [specialInstruction, setSpecialInstruction] = useState('')
  const [pastedImage,       setPastedImage]       = useState(null)   // base64 or null
  const [error,             setError]             = useState('')

  // Approver list — fetched from /common_controller/get_em_above
  // Shape: [{ id, name, designation }]
  const [approvers,         setApprovers]         = useState([])
  const [approversLoading,  setApproversLoading]  = useState(false)

  // Countries list — fetched from /common_controller/country_region_map
  // Raw API: [{ region_id, region_name, country_id, country }, ...]
  // Grouped into: [{ region_id, region_name, countries: [{ country_id, country }] }]
  const [countries,         setCountries]         = useState([])
  const [countriesLoading,  setCountriesLoading]  = useState(false)

  // ── fetch approvers + countries when modal opens ──────────────────────────
  useEffect(() => {
    if (!show) return

    const headers = { Authorization: `Bearer ${token}` }

    // Approvers
   const fetchApprovers = async () => {
  setApproversLoading(true)
  try {
    const res = await axios.get(
      `${API_BASE_URL}/common_controller/get_em_above`,
      { headers }
    )

    let users = res.data || []

    // If API returns { data: [...] }
    if (users.data) {
      users = users.data
    }

    // 🔥 Ensure 401 is always present
    // const forcedUser = { id: 401, name: "Ashutosh Kumar" } // change label if needed

    // users = [
    //   forcedUser,
    //   ...users.filter(u => String(u.id) !== "401")
    // ]

    setApprovers(users)

  } catch (err) {
    console.error('Error fetching approvers', err)
  } finally {
    setApproversLoading(false)
  }
}

    // Countries — group flat rows by region for <optgroup> rendering
    const fetchCountries = async () => {
      setCountriesLoading(true)
      try {
        const res = await axios.get(`${API_BASE_URL}/common_controller/country_region_map`, { headers })
        const rows = res.data || []

        // Group: { region_id, region_name, countries: [{ country_id, country }] }
        const groupMap = {}
        rows.forEach(row => {
          if (!groupMap[row.region_id]) {
            groupMap[row.region_id] = { region_id: row.region_id, region_name: row.region_name, countries: [] }
          }
          if (row.country_id) {
            groupMap[row.region_id].countries.push({ country_id: row.country_id, country: row.country })
          }
        })
        setCountries(Object.values(groupMap))
      } catch (err) {
        console.error('Error fetching countries', err)
      } finally {
        setCountriesLoading(false)
      }
    }

    fetchApprovers()
    fetchCountries()
  }, [show])

  // ── working days (display only — not sent to backend) ─────────────────────
  const workingDays = (() => {
    if (tripType === 'oneway') return legs[0]?.departure ? 1 : 0
    if (tripType === 'round') {
      const { departure: dep, returnDate: ret } = legs[0] || {}
      return dep && ret ? getWorkingDays(dep, ret) : 0
    }
    const dates = legs.map(l => l.departure).filter(Boolean).sort()
    if (dates.length >= 2) return getWorkingDays(dates[0], dates[dates.length - 1])
    return dates.length === 1 ? 1 : 0
  })()

  // ── trip type ─────────────────────────────────────────────────────────────
  const handleTripType = (t) => {
    setTripType(t)
    setError('')
    if (t !== 'multi' && legs.length > 1) setLegs([legs[0]])
  }

  // ── leg CRUD ──────────────────────────────────────────────────────────────
  const updateLeg = useCallback((id, key, val) => {
    setLegs(prev => prev.map(l => l.id === id ? { ...l, [key]: val } : l))
    setError('')
  }, [])

  const addLeg = () => {
    if (legs.length >= 6) { setError('Maximum 6 legs allowed.'); return }
    setLegs(prev => [...prev, newLeg()])
  }

  const removeLeg = (id) => {
    setLegs(prev => prev.length > 1 ? prev.filter(l => l.id !== id) : prev)
  }

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!approver) return 'Please select an approver.'
    for (let i = 0; i < legs.length; i++) {
      const l = legs[i]
      if (!l.cityFrom || !l.cityTo || !l.departure)
        return `Leg ${i + 1}: please fill city names and departure date.`
      if (tripType === 'round' && i === 0 && !l.returnDate)
        return 'Please select a return date.'
      if (tripType === 'round' && i === 0 && l.returnDate < l.departure)
        return 'Return date cannot be before departure.'
      if (isWeekend(l.departure))
        return `Leg ${i + 1}: departure date cannot be a weekend.`
      if (tripType === 'round' && isWeekend(l.returnDate))
        return 'Return date cannot be a weekend.'
    }
    return null
  }

  // ── reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    _legId = 0
    setTripType('round')
    setLegs([newLeg()])
    setApprover('')
    setSpecialInstruction('')
    setPastedImage(null)
    setError('')
  }

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const clientErr = validate()
    if (clientErr) { setError(clientErr); return }
    setError('')

    const payload = buildPayload(
      tripType,
      legs,
      employeeinfo_id,
      approver,
      [approver],           // notification: approver always notified
      specialInstruction,
      pastedImage,          // base64 — controller renames to 'screenshot'
    )

    setGlobalLoading(true)
    try {
      const res = await axios.post(
        `${API_BASE_URL}/employeeinfos/travel/apply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      )

      if (res.status === 200 || res.status === 201) {
        toastr.success(res.data.message || 'Travel request submitted successfully')
        setShow(false)
        resetForm()
        fetchTravels()
      }
    } catch (err) {
      console.error(err?.response?.data?.message || 'Failed to submit travel request')
    } finally {
      setGlobalLoading(false)
    }
  }

  const close = () => { setShow(false); resetForm() }

  const tripBtnStyle = (key) => ({
    padding: '9px 6px',
    borderRadius: 9,
    border:     tripType === key ? '1.5px solid #1d6fb8' : '1.5px solid #e5e7eb',
    background: tripType === key ? '#e8f1fb'             : '#f9fafb',
    color:      tripType === key ? '#1d6fb8'             : '#6b7280',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all .15s',
  })

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,30,60,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 500, backdropFilter: 'blur(4px)',
            padding: 20, boxSizing: 'border-box',
          }}
        >
          <motion.div
            key="modal"
            initial={{ scale: 0.88, y: 36, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.88, y: 36, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 18,
              width: '100%', maxWidth: 560,
              maxHeight: '90vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div style={{
              padding: '20px 26px 16px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10, background: '#1d6fb8',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
              }}>
                {'\u2708\uFE0F'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0c1f3a' }}>
                  New Travel Request
                </h3>
                <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
                  Complete all fields to submit
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  border: '1px solid #e5e7eb', background: '#f9fafb',
                  cursor: 'pointer', fontSize: 14, color: '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {'\u2715'}
              </button>
            </div>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <div style={{ padding: '4px 26px 20px', overflowY: 'auto', flex: 1 }}>

              {/* Error banner */}
              {error && (
                <div style={{
                  margin: '12px 0 4px', background: '#fef2f2', color: '#b91c1c',
                  padding: '10px 14px', borderRadius: 9, fontSize: 13,
                  border: '1px solid #fca5a5',
                }}>
                  {'\u26A0\uFE0F'} {error}
                </div>
              )}

              {/* Trip type */}
              <SectionDivider label="Trip type" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 4 }}>
                {TRIP_TYPES.map(({ key, label }) => (
                  <button key={key} type="button" onClick={() => handleTripType(key)} style={tripBtnStyle(key)}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Route legs */}
              <SectionDivider label="Route" />
              {legs.map((leg, i) => (
                <LegRow
                  key={leg.id}
                  leg={leg}
                  index={i}
                  tripType={tripType}
                  showRemove={legs.length > 1}
                  onUpdate={updateLeg}
                  onRemove={removeLeg}
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              ))}

              {tripType === 'multi' && (
                <button
                  type="button"
                  onClick={addLeg}
                  style={{
                    width: '100%', padding: '9px', borderRadius: 9,
                    border: '1.5px dashed #d1d5db', background: 'transparent',
                    fontSize: 13, color: '#6b7280', cursor: 'pointer',
                    fontFamily: 'inherit', marginBottom: 4,
                  }}
                >
                  + Add another leg
                </button>
              )}

              {/* Approval */}
              <SectionDivider label="Approval" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Approver">
                  <select
                    value={approver}
                    onChange={e => setApprover(e.target.value)}
                    disabled={approversLoading}
                    style={{ ...inp, opacity: approversLoading ? 0.6 : 1 }}
                  >
                    <option value="">{approversLoading ? 'Loading...' : 'Select approver'}</option>
                    {approvers.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} — {a.designation}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Working days">
                  <input
                    type="number"
                    value={workingDays}
                    disabled
                    style={{ ...inp, opacity: 0.6, cursor: 'not-allowed' }}
                  />
                </Field>
              </div>

              {/* Special instruction — matches PHP form field */}
              <SectionDivider label="Special instruction for travel desk" />
              <textarea
                value={specialInstruction}
                onChange={e => setSpecialInstruction(e.target.value)}
                placeholder="eg. Book flight before 6 a.m. departure"
                rows={3}
                style={{
                  ...inp,
                  resize: 'vertical',
                  paddingTop: 10,
                  paddingBottom: 10,
                  lineHeight: 1.6,
                  minHeight: 80,
                }}
              />

              {/* Paste screenshot — matches PHP paste-image area */}
              <SectionDivider label="Travel approval screenshot" />
              <PasteImageArea image={pastedImage} onImage={setPastedImage} />

            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div style={{
              padding: '14px 26px',
              borderTop: '1px solid #f1f5f9',
              display: 'flex', gap: 10,
            }}>
              <button
                type="button"
                onClick={close}
                style={{
                  flex: 1, padding: 10, borderRadius: 9,
                  border: '1.5px solid #e5e7eb', background: '#fff',
                  fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 14, color: '#6b7280',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                style={{
                  flex: 2, padding: 10, borderRadius: 9,
                  border: 'none', background: '#1d6fb8', color: '#fff',
                  fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 14,
                }}
              >
                Submit Request
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}